import { useId, useRef, type KeyboardEvent, type ReactNode } from 'react';
import { cx } from './cx';
import s from './Tabs.module.css';

export interface TabItem {
  id: string;
  label: ReactNode;
  count?: number;
  disabled?: boolean;
}

export interface TabsProps {
  items: TabItem[];
  value: string;
  onChange: (id: string) => void;
  'aria-label'?: string;
  /** Contenu du panneau actif (rendu avec les attributs ARIA adéquats). */
  children?: ReactNode;
}

export function Tabs({ items, value, onChange, children, ...rest }: TabsProps) {
  const base = useId();
  const refs = useRef<Record<string, HTMLButtonElement | null>>({});
  const enabled = items.filter((i) => !i.disabled);

  const onKeyDown = (e: KeyboardEvent) => {
    const idx = enabled.findIndex((i) => i.id === value);
    let next = -1;
    // En RTL, les flèches gauche/droite sont inversées visuellement : on s'appuie sur la direction du document
    const rtl = document.documentElement.dir === 'rtl';
    const forward = rtl ? 'ArrowLeft' : 'ArrowRight';
    const back = rtl ? 'ArrowRight' : 'ArrowLeft';
    if (e.key === forward) next = (idx + 1) % enabled.length;
    else if (e.key === back) next = (idx - 1 + enabled.length) % enabled.length;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = enabled.length - 1;
    if (next >= 0) {
      e.preventDefault();
      const target = enabled[next];
      onChange(target.id);
      refs.current[target.id]?.focus();
    }
  };

  return (
    <div>
      <div role="tablist" aria-label={rest['aria-label']} className={s.list} onKeyDown={onKeyDown}>
        {items.map((it) => {
          const active = it.id === value;
          return (
            <button
              key={it.id}
              ref={(el) => {
                refs.current[it.id] = el;
              }}
              role="tab"
              type="button"
              id={`${base}-tab-${it.id}`}
              aria-selected={active}
              aria-controls={`${base}-panel-${it.id}`}
              tabIndex={active ? 0 : -1}
              disabled={it.disabled}
              className={cx(s.tab, active && s.active)}
              onClick={() => onChange(it.id)}
            >
              {it.label}
              {typeof it.count === 'number' && <span className={s.count}>{it.count}</span>}
            </button>
          );
        })}
      </div>
      {children !== undefined && (
        <div role="tabpanel" id={`${base}-panel-${value}`} aria-labelledby={`${base}-tab-${value}`} className={s.panel}>
          {children}
        </div>
      )}
    </div>
  );
}
